import React, { useEffect, useState } from 'react';
import { useAppContext } from '../../share-components/AppContext';
import { useNavigate } from 'react-router-dom';
import { ROUTE_MONEY_ADJUSTMENT_DETAILS, ROUTE_MONEY_ADJUSTMENT_LIST } from '../../constants/routeConstants';
import * as Yup from 'yup';
import { Button, Card } from 'react-bootstrap';
import { customStorage, getCurrentJSTDate, getPayloadData, scrollToFormError } from '../../services/UtilityService';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import AlertDialog from '../../share-components/AlertDialog';
import ConfirmDialog from '../../share-components/ConfirmDialog';
import { getAllActiveUsers, getLastLockedYearMonth } from '../../services/CommonService';
import FormTitle from '../../share-components/FormTitle';

// ? NEW IMPORTS
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import CalendarInput from '../../share-components/CalendarInput';
import { SERVER_ERROR_MESSAGE, TOKEN_SESSION_KEY } from '../../constants/commonConstants';
import { addMoneyAdjustment } from '../../services/MoneyAdjustmentService';

const getLastMonthDate = () => {
    const jst = getCurrentJSTDate();
    jst.setMonth(jst.getMonth() - 1); // subtract 1 month
    return jst;
};

const AddMoneyAdjustment = () => {
    const token = customStorage.getItem(TOKEN_SESSION_KEY);
    const currentUser = getPayloadData(token);

    const { setLoading, setShowApiErrorMessage, setRouteNaviData } = useAppContext();
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [formValue, setFormValue] = useState(null);
    const [isFirstLoaded, setIsFirstLoaded] = useState(false);
    const [createdMoneyAdjustmentId, setCreatedMoneyAdjustmentId] = useState(null);
    const [minDate, setMinDate] = useState(getLastMonthDate);
    const [activeUsers, setActiveUsers] = useState([]);
    const navigate = useNavigate();

    const now = getCurrentJSTDate();
    const maxDate = now;
    const today = maxDate;

    const initialLoad = async () => {
        setLoading(true);
        try {
            const response = await getLastLockedYearMonth();
            if (response) {
                const minDate = new Date(response?.toYear, (parseInt(response?.toMonth) || 0), 1);
                setMinDate(minDate);
            }
            const users = await getAllActiveUsers();
            const sortedUsers = (users || [])?.sort((a, b) => (a.username || "")?.localeCompare((b?.username || "")));
            const filteredUser = sortedUsers.filter(user=> user.userId !== currentUser?.userId);
            setActiveUsers(filteredUser);
            setIsFirstLoaded(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setRouteNaviData("MONEY ADJUSTMENT >> NEW ADJUSTMENT");
        initialLoad();
    }, []);

    const initialValues = {
        paymentAmount: '',
        paymentDate: '',
        acceptedById: '',
        comment: ''
    };

    const validationSchema = Yup.object({
        paymentAmount: Yup.number().nullable()
            .required('Payment Amount is required.')
            .transform((value) => (Number.isNaN(value) ? null : value))
            .min(1, 'Payment Amount must exceed 0.')
            .max(1000000, 'Payment Amount must not exceed 1000000.'),
        paymentDate: Yup.date()
            .required('Payment Date is required.')
            .max(maxDate, 'Payment Date cannot be in the future.')
            .min(minDate, `Payment Date cannot be before ${format(minDate, 'yyyyMMdd')}`),
        acceptedById: Yup.string().nullable()
            .required('Accepted By is required.'),
        comment: Yup.string().nullable()
            .max(300, 'Comment must not exceed 300 characters.')
    });

    const onSubmit = async (form) => {
        const payload = {
            ...form,
            paymentDate: format(form.paymentDate, 'yyyyMMdd') // ? send formatted string
        };
        setFormValue(payload);
        setShowConfirmDialog(true);
    };

    const handleAddMoneyAdjustment = async () => {
        setShowConfirmDialog(false);
        setLoading(true);
        try {
            const createdId = await addMoneyAdjustment(formValue);
            setCreatedMoneyAdjustmentId(createdId);
            setShowSuccessDialog(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSuccessDialogClose = () => {
        setShowSuccessDialog(false);
        if ((parseInt(createdMoneyAdjustmentId) || 0) > 0) {
            navigate(`${ROUTE_MONEY_ADJUSTMENT_DETAILS}/${createdMoneyAdjustmentId}`);
        } else {
            navigate(ROUTE_MONEY_ADJUSTMENT_LIST);
        }
    };

    return (
        <div id='addMoneyAdjustmentPage' style={{ paddingBottom: '50px' }}>
            <FormTitle title={"New Adjustment"} isCenter={true} />

            {isFirstLoaded && <>
                <Card style={{ padding: '10px', margin: '0 auto', maxWidth: '650px' }}>
                    <Card.Body className='pt-2'>
                        <div className="container">
                            <div className="row justify-content-center">
                                <div style={{ maxWidth: "550px" }}>
                                    <Formik
                                        initialValues={initialValues}
                                        validationSchema={validationSchema}
                                        onSubmit={onSubmit}>
                                        {({ setFieldValue, values }) => (
                                            <Form>
                                                <div className="mb-1">
                                                    <label className='custom-label'>Payment Amount*</label>
                                                    <Field
                                                        name="paymentAmount"
                                                        type="number"
                                                        inputMode="numeric"  // forces numeric keypad on mobile
                                                        pattern="[0-9]*"      // enforces only digits
                                                        className="w-100 customInputControl clearNumberSpin"
                                                        onKeyDown={(e) => {
                                                            if (["e", "-", ".", "+"].includes(e.key)) {
                                                                e.preventDefault();
                                                            }
                                                        }}
                                                        onWheel={(e) => e.target.blur()}
                                                    />
                                                    <ErrorMessage name="paymentAmount" component="div" className="formErrDiv text-danger" />
                                                </div>

                                                <div className="mb-1">
                                                    <label className='custom-label'>Payment Date(yyyyMMdd)*</label><br />
                                                    <DatePicker
                                                        selected={values.paymentDate}
                                                        onChange={(date) => setFieldValue("paymentDate", date)}
                                                        dateFormat="yyyy/MM/dd"
                                                        minDate={minDate}
                                                        maxDate={maxDate}
                                                        placeholderText="Select date"
                                                        customInput={<CalendarInput isReadOnly={true} />}  // 👈 here
                                                        dayClassName={(date) =>
                                                            date.getDate() === today.getDate() &&
                                                                date.getMonth() === today.getMonth() &&
                                                                date.getFullYear() === today.getFullYear()
                                                                ? "current-day"
                                                                : undefined
                                                        }
                                                    />
                                                    <ErrorMessage name="paymentDate" component="div" className="formErrDiv text-danger" />
                                                </div>

                                                <div className="mb-1">
                                                    <label className="custom-label">Accepted By*</label>
                                                    <Field as="select" name="acceptedById" className="w-100 customInputControl">
                                                        <option value="" disabled>--Select--</option>
                                                        {activeUsers.map(user => (
                                                            <option key={user.userId} value={user.userId}>{user.username}</option>
                                                        ))}
                                                    </Field>
                                                    <ErrorMessage name="acceptedById" component="div" className="formErrDiv text-danger" />
                                                </div>

                                                <div className="mb-1">
                                                    <label className='custom-label'>Comment</label>
                                                    <Field name="comment" component="textarea" rows="4" className="w-100 customInputControl" autoComplete="off" />
                                                    <ErrorMessage name="comment" component="div" className="formErrDiv text-danger" />
                                                </div>

                                                <div className='floating-bottom-btn-group mt-2'>
                                                    <div className='floating-bottom-btn-grid'>
                                                        <Button
                                                            className="w-100"
                                                            type="submit"
                                                            variant="primary"
                                                            onClick={scrollToFormError}
                                                        >
                                                            Save
                                                        </Button>
                                                    </div>
                                                </div>
                                            </Form>
                                        )}
                                    </Formik>
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                <ConfirmDialog
                    dialogId='addMoneyAdjustmentConfirmDialog'
                    showDialog={showConfirmDialog}
                    body='Are you sure to add new money adjustment?'
                    onConfirm={handleAddMoneyAdjustment}
                    onCancel={() => { setFormValue(null); setShowConfirmDialog(false); }}
                />
                <AlertDialog
                    dialogId={'successDialog'}
                    showDialog={showSuccessDialog}
                    body={'Your Money Adjustment has been successfully added.'}
                    onConfirm={handleSuccessDialogClose}
                />
            </>}
        </div>
    );
};

export default AddMoneyAdjustment;
